export interface AddonPriceSource {
  default_cost: number | string;
  payment_period: string;
  payment_frequency?: string | null;
}

export interface SelectedAddon {
  addon: AddonPriceSource;
  quantity?: number | string | null;
}

// An add-on as the server offers it, priced the way the firm set it up.
export interface AddonOffer {
  id: number;
  name: string;
  price: number;
  payment_period: string;
  payment_frequency?: string | null;
  frequency: string;
  per_employee: boolean;
  quantity?: number;
}

export const DEFAULT_CONTRACT_MONTHS = 12;
export const ONE_TIME_LABEL = "One-time";

// What the client is charged when they never picked a cadence, same as packagePricing.ts.
const DEFAULT_PAYMENT_TERM = "MONTH";

const INSTALMENTS_PER_YEAR: Record<string, number> = {
  week: 52,
  MONTH: 12,
  quarter: 4,
  semi_annually: 2,
  year: 1,
};

const MONTHS_PER_CHARGE: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  quarter: 3,
  annually: 12,
  annual: 12,
  yearly: 12,
};

const FREQUENCY_LABELS: Record<number, string> = {
  1: "Monthly",
  3: "Quarterly",
  12: "Annual",
};

const isOneTime = (addon: AddonPriceSource): boolean =>
  String(addon.payment_period || "")
    .toLowerCase()
    .replace("-", " ")
    .trim() === "one time";

const monthsPerCharge = (addon: AddonPriceSource): number | undefined =>
  MONTHS_PER_CHARGE[String(addon.payment_frequency || "").toLowerCase().trim()];

const normalizeQuantity = (quantity: SelectedAddon["quantity"]): number => {
  const value = parseInt(String(quantity ?? ""), 10);
  return value > 0 ? value : 1;
};

export const getAddonContractValue = (
  { addon, quantity }: SelectedAddon,
  contractMonths: number = DEFAULT_CONTRACT_MONTHS
): number => {
  const cost = Number(addon.default_cost) || 0;
  if (isOneTime(addon)) {
    return cost * normalizeQuantity(quantity);
  }
  const months = monthsPerCharge(addon);
  // The roadmap's formula counts an unknown frequency as $0, which would show the client a
  // price lower than what the server charges for the same add-on.
  if (!months) {
    throw new Error(`Unknown payment frequency "${addon.payment_frequency}"`);
  }
  return ((cost * contractMonths) / months) * normalizeQuantity(quantity);
};

// Mirrors BxBlockPricingcalculator::AddonPricing on the server, which is what actually charges.
export const getAddonInstalmentIncrement = (
  addons: SelectedAddon[],
  paymentTerm: string | null,
  contractMonths: number = DEFAULT_CONTRACT_MONTHS
): number => {
  const months = contractMonths > 0 ? contractMonths : DEFAULT_CONTRACT_MONTHS;
  const perYear =
    INSTALMENTS_PER_YEAR[paymentTerm || ""] ||
    INSTALMENTS_PER_YEAR[DEFAULT_PAYMENT_TERM];
  const total = addons.reduce(
    (sum, selected) => sum + getAddonContractValue(selected, months),
    0
  );
  return Math.round((total / ((months * perYear) / 12)) * 100) / 100;
};

export const toSelectedAddons = (offers?: AddonOffer[] | null): SelectedAddon[] =>
  (offers || []).map((offer) => ({
    addon: {
      default_cost: offer.price,
      payment_period: offer.payment_period,
      payment_frequency: offer.payment_frequency,
    },
    quantity: offer.quantity,
  }));

export const getAddonContractTotal = (
  addons: SelectedAddon[],
  contractMonths: number = DEFAULT_CONTRACT_MONTHS
): number => {
  const months = contractMonths > 0 ? contractMonths : DEFAULT_CONTRACT_MONTHS;
  const total = addons.reduce(
    (sum, selected) => sum + getAddonContractValue(selected, months),
    0
  );
  return Math.round(total * 100) / 100;
};

export const getAddonFrequencyLabel = (addon: AddonPriceSource): string => {
  if (isOneTime(addon)) {
    return ONE_TIME_LABEL;
  }
  const months = monthsPerCharge(addon);
  return months ? FREQUENCY_LABELS[months] : String(addon.payment_frequency || "");
};
